/**
 * Utilidades para sincronizar filtros del catálogo de slots con la URL.
 * Soporta sortBy/sortDirection y parámetros legacy (sortOrder + rankDir/nameDir/launchDir).
 */
import { SORT_OPTIONS } from '../components/top-bar/search-bar'
import firstBy from 'thenby'

/** Campos de ordenamiento disponibles, derivados de las opciones del search bar. */
export const SORT_FIELDS = SORT_OPTIONS.map((option) => option.value)

export const DEFAULT_SORT_BY = 'rank'
export const DEFAULT_SORT_DIRECTION = 1

/** Dirección por defecto (1 = asc, -1 = desc) al activar cada campo por primera vez. */
export const DEFAULT_SORT_DIRECTIONS = {
  rank: 1,
  name: 1,
  launch_date: -1,
}

/** Construye un comparador simple por un solo campo y dirección. */
export const buildCatalogSorter = (
  sortBy = DEFAULT_SORT_BY,
  sortDirection = DEFAULT_SORT_DIRECTION
) => {
  return firstBy(sortBy, sortDirection)
}

/** Query params que este módulo controla; se eliminan antes de reescribir la URL. */
const MANAGED_PARAM_KEYS = [
  'type',
  'searchText',
  'sortBy',
  'sortDirection',
  'sortOrder',
  'rankDir',
  'nameDir',
  'launchDir',
]

/** Mapeo campo de sort → nombre del param de dirección en la URL legacy. */
const DIR_PARAM_BY_FIELD = {
  rank: 'rankDir',
  name: 'nameDir',
  launch_date: 'launchDir',
}

/** Normaliza dirección de sort: solo acepta 1 (asc) o -1 (desc). */
const parseDirection = (value, fallback = 1) => {
  const parsed = Number(value)

  if (parsed === 1 || parsed === -1) {
    return parsed
  }

  return fallback
}

const normalizeSelectedType = (value) => {
  if (!value || value === 'all') {
    return 'all'
  }

  return value
}

const parseLegacySortBy = (params) => {
  const sortBy = params.get('sortBy')

  return SORT_FIELDS.includes(sortBy) ? sortBy : null
}

const parseLegacySortFromOrder = (params) => {
  const sortOrderValue = params.get('sortOrder')

  if (!sortOrderValue) {
    return null
  }

  const primaryField = sortOrderValue
    .split(',')
    .map((field) => field.trim())
    .find((field) => SORT_FIELDS.includes(field))

  if (!primaryField) {
    return null
  }

  const directionParam = DIR_PARAM_BY_FIELD[primaryField]
  const direction = params.has(directionParam)
    ? parseDirection(params.get(directionParam))
    : DEFAULT_SORT_DIRECTIONS[primaryField]

  return {
    sortBy: primaryField,
    sortDirection: direction,
  }
}

/** Lee el query string y devuelve el estado normalizado de filtros del catálogo. */
export const parseCatalogFilters = (search = '') => {
  const params = new URLSearchParams(search)
  const legacySortBy = parseLegacySortBy(params)
  const legacySortDirection = params.has('sortDirection')
    ? parseDirection(params.get('sortDirection'))
    : null
  const legacySortFromOrder = parseLegacySortFromOrder(params)

  let sortBy = legacySortBy || legacySortFromOrder?.sortBy || DEFAULT_SORT_BY
  let sortDirection =
    legacySortDirection ??
    legacySortFromOrder?.sortDirection ??
    DEFAULT_SORT_DIRECTIONS[sortBy] ??
    DEFAULT_SORT_DIRECTION

  if (legacySortBy && legacySortDirection !== null) {
    sortDirection = legacySortDirection
  }

  return {
    selectedType: normalizeSelectedType(params.get('type')),
    searchText: params.get('searchText') || '',
    sortBy,
    sortDirection,
  }
}

/**
 * Serializa filtros a query string. Omite valores por defecto para mantener
 * la URL limpia y preserva params ajenos al catálogo (currentSearch).
 */
export const buildCatalogSearch = (filters, currentSearch = '') => {
  const params = new URLSearchParams(currentSearch)

  MANAGED_PARAM_KEYS.forEach((key) => {
    params.delete(key)
  })

  if (filters.selectedType !== 'all') {
    params.set('type', filters.selectedType)
  }

  if (filters.searchText) {
    params.set('searchText', filters.searchText)
  }

  if (filters.sortBy !== DEFAULT_SORT_BY) {
    params.set('sortBy', filters.sortBy)
  }

  const fieldDefaultDirection =
    DEFAULT_SORT_DIRECTIONS[filters.sortBy] ?? DEFAULT_SORT_DIRECTION

  if (filters.sortDirection !== fieldDefaultDirection) {
    params.set('sortDirection', String(filters.sortDirection))
  }

  const search = params.toString()

  return search ? `?${search}` : ''
}

/** Compara dos estados de filtros para evitar actualizaciones redundantes de URL/estado. */
export const areCatalogFiltersEqual = (left, right) =>
  left.selectedType === right.selectedType &&
  left.searchText === right.searchText &&
  left.sortBy === right.sortBy &&
  left.sortDirection === right.sortDirection
